import { defineStore } from 'pinia';
import { apiCall } from '@/utils/globalFunctions';
import store from '@/store/store';

export const useTemplateStore = defineStore('templates', {
  state: () => ({
    userId: store.state.user?.user_id || null,
    templates: [],
    loading: false,
    error: null,
    availableVariables: [],
    currentTemplate: null,
  }),

  getters: {
    filteredTemplates: state => state.templates,
    getTemplateById: state => id => state.templates.find(t => t.template_id === id),
    defaultTemplates: state => state.templates.filter(t => t.is_default),
    getDefaultTemplateByType: state => type => state.templates.find(t => t.type === type && t.is_default),
  },

  actions: {
    setUserId(userId) {
      this.userId = userId;
      this.templates = [];
      this.currentTemplate = null;
      this.loadFromLocalStorage();
      if (userId) {
        this.fetchTemplates(); // optional: keep if you want fresh data after login
      }
    },

    reset() {
      if (this.userId) {
        localStorage.removeItem(`templates_${this.userId}`);
      }
      this.$reset();
      this.userId = null;
    },

    persistToLocalStorage() {
      if (this.userId) {
        localStorage.setItem(`templates_${this.userId}`, JSON.stringify(this.templates));
      }
    },

    loadFromLocalStorage() {
      if (!this.userId) return;
      const saved = localStorage.getItem(`templates_${this.userId}`);
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          this.templates = parsed.map(t => ({
            ...t,
            is_default: t.is_default === true || t.is_default === '1',
          }));
        } catch {
          localStorage.removeItem(`templates_${this.userId}`);
        }
      }
    },

    setCurrentTemplate(template) {
      this.currentTemplate = template ? { ...template } : null;
    },

    setAvailableVariables(variables) {
      this.availableVariables = variables;
    },

    resetError() {
      this.error = null;
    },

    setError(err, fallbackMessage, fallbackCode) {
      this.error = {
        message: err?.message || fallbackMessage,
        code: err?.code || fallbackCode,
        timestamp: new Date().toISOString(),
      };
    },

    async refreshTemplates() {
      this.loading = true;
      try {
        await this.fetchTemplates();
      } finally {
        this.loading = false;
      }
    },

    async fetchTemplates() {
      if (!this.userId) {
        this.setError(null, 'No user logged in', 'NO_USER');
        return [];
      }
      this.loading = true;
      this.resetError();

      try {
        const res = await apiCall({ url: '/Template/getTemplates', method: 'GET' }, store);
        if (Array.isArray(res.data)) {
          this.templates = res.data.map(c => ({
            ...c,
            is_default: c.is_default === '1' ? true : false,
          }));
          this.persistToLocalStorage();
          return res.data;
        }
        throw new Error(res.message ?? 'Invalid template data');
      } catch (err) {
        this.setError(err, 'Failed to fetch templates', 'FETCH_TEMPLATES_FAILED');
        throw err;
      } finally {
        this.loading = false;
      }
    },

    async fetchDefaultTemplate(type) {
      if (!this.userId) {
        this.setError(null, 'No user logged in', 'NO_USER');
      }
      try {
        const res = await apiCall({ url: '/Template/getDefaultTemplate', method: 'GET', data: { type } }, store);
        if (res.data) return res.data;
        throw new Error(res.message ?? 'No default template found');
      } catch (err) {
        this.setError(err, 'Failed to fetch default template', 'FETCH_DEFAULT_FAILED');
        throw err;
      }
    },

    async addTemplate(templateData) {
      if (!this.userId) {
        this.setError(null, 'No user logged in', 'NO_USER');
      }
      this.loading = true;
      this.resetError();

      try {
        const res = await apiCall({ url: '/Template/addTemplate', method: 'POST', data: templateData }, store);
        if (res.status === 'success') {
          await this.fetchTemplates();
          return res;
        }
        throw new Error(res.message ?? 'Failed to add template');
      } catch (err) {
        this.setError(err, 'Failed to add template', 'ADD_TEMPLATE_FAILED');
        throw err;
      } finally {
        this.loading = false;
      }
    },

    async updateTemplate(templateData) {
      if (!this.userId) {
        this.setError(null, 'No user logged in', 'NO_USER');
      }
      this.loading = true;
      this.resetError();

      try {
        const res = await apiCall({ url: '/Template/updateTemplate', method: 'POST', data: templateData }, store);
        if (res.status === 'success') {
          await this.fetchTemplates();
          return res;
        }
        throw new Error(res.message ?? 'Failed to update template');
      } catch (err) {
        this.setError(err, 'Failed to update template', 'UPDATE_TEMPLATE_FAILED');
        throw err;
      } finally {
        this.loading = false;
      }
    },

    async setDefaultTemplate(templateId) {
      if (!this.userId) {
        this.setError(null, 'No user logged in', 'NO_USER');
      }
      this.loading = true;
      this.resetError();

      try {
        const res = await apiCall({ url: '/Template/setDefaultTemplates', method: 'POST', data: { template_id: templateId } }, store);
        if (res.status === 'success') {
          await this.fetchTemplates();
          return res;
        }
        throw new Error(res.message ?? 'Failed to set default template');
      } catch (err) {
        this.setError(err, 'Failed to set default template', 'SET_DEFAULT_FAILED');
        throw err;
      } finally {
        this.loading = false;
      }
    },

    async deleteTemplate(templateId) {
      if (!this.userId) {
        this.setError(null, 'No user logged in', 'NO_USER');
      }
      this.loading = true;
      this.resetError();

      try {
        const res = await apiCall({ url: '/Template/deleteTemplate', method: 'POST', data: { template_id: templateId } }, store);
        if (res.status === 'success') {
          this.templates = this.templates.filter(t => t.template_id !== templateId);
          this.persistToLocalStorage();
          return res;
        }
        throw new Error(res.message ?? 'Failed to delete template');
      } catch (err) {
        this.setError(err, 'Failed to delete template', 'DELETE_TEMPLATE_FAILED');
        throw err;
      } finally {
        this.loading = false;
      }
    },
  },
});
